import {
  Color,
  DynamicDrawUsage,
  Group,
  InstancedMesh,
  Material,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Quaternion,
  Texture,
  Vector3,
} from "three";
import type { BufferGeometry } from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

import {
  VILNIUS_MODULAR_CITY_ASSET_LOAD_CONCURRENCY,
  VILNIUS_MODULAR_CITY_BUCKET_SIZE_M,
  VILNIUS_MODULAR_CITY_FULL_DETAIL_DISTANCE_M,
  VILNIUS_MODULAR_CITY_MAXIMUM_DISTANCE_M,
  VILNIUS_MODULAR_CITY_VISIBILITY_MARGIN_M,
  VILNIUS_MODULAR_CITY_VISIBILITY_REFRESH_DISTANCE_M,
} from "../../config/modularCityAssets.ts";
import {
  authoredMaterialSeasonTreatment,
} from "../../config/seasons.ts";
import type { EnvironmentSeason } from "../environment.ts";
import type { RenderVector3 } from "../types.ts";

export interface ModularCityAssetArchetype {
  readonly id: string;
  readonly assetUrl: string;
  readonly lod1Url: string;
}

export interface ModularCityAssetPlacement {
  readonly id: string;
  readonly label: string;
  readonly archetypeId: string;
  readonly category: string;
  readonly tileId: string;
  readonly position: RenderVector3;
  readonly rotationYRadians: number;
  readonly scale: RenderVector3;
}

export interface ModularCityAssetManifest {
  readonly schemaVersion: 1;
  readonly sourceManifest: string;
  readonly archetypes: readonly ModularCityAssetArchetype[];
  readonly placements: readonly ModularCityAssetPlacement[];
}

export interface ModularCityAssetDiagnostics {
  readonly archetypeCount: number;
  readonly placementCount: number;
  readonly bucketCount: number;
  readonly meshCount: number;
  readonly templateTriangleCount: number;
  readonly instancedTriangleCount: number;
  readonly fullDetailArchetypeCount: number;
  readonly visibleBucketCount: number;
  readonly fullDetailBucketCount: number;
  readonly categoryCounts: Readonly<Record<string, number>>;
}

export interface ModularCityAssetLoadOptions {
  /** Render-layer preparation that must finish before a streamed level is revealed. */
  readonly prepareObjectForDisplay?: (object: Object3D) => Promise<void>;
  /** Share one background decode lane across independent authored collections. */
  readonly scheduleDeferredTask?: <T>(task: () => Promise<T>) => Promise<T>;
}

export interface ModularCityAssetCollection {
  readonly object: Group;
  readonly diagnostics: ModularCityAssetDiagnostics;
  setSeason(season: EnvironmentSeason): void;
  update(
    focus: Readonly<RenderVector3>,
    visibleDistanceMeters?: number,
  ): void;
  startDeferredStreaming(): void;
  loadDeferred(): Promise<void>;
  dispose(): void;
}

interface TemplateMesh {
  readonly geometry: BufferGeometry;
  readonly material: Material | readonly Material[];
  readonly matrix: Matrix4;
  readonly castShadow: boolean;
  readonly triangleCount: number;
}

interface AssetBucket {
  readonly key: string;
  readonly center: Readonly<{ x: number; z: number }>;
  readonly placements: readonly ModularCityAssetPlacement[];
}

interface InstancedLevel {
  readonly object: Group;
  readonly meshes: readonly InstancedMesh[];
  readonly templates: readonly TemplateMesh[];
}

const WORLD_UP = new Vector3(0, 1, 0);

interface ArchetypeRuntime {
  readonly archetype: ModularCityAssetArchetype;
  readonly placements: readonly ModularCityAssetPlacement[];
  readonly buckets: readonly AssetBucket[];
  readonly simplified: InstancedLevel;
  full: InstancedLevel | null;
  fullTemplates: readonly TemplateMesh[] | null;
  fullLoad: Promise<void> | null;
  fullLoaded: boolean;
}

function requireRecord(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value as Record<string, unknown>;
}

function requireString(value: unknown, label: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`${label} must be a non-empty string`);
  }
  return value;
}

function requireNumber(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${label} must be finite`);
  }
  return value;
}

function requireScaleNumber(value: unknown, label: string): number {
  const scale = requireNumber(value, label);
  if (scale <= 0 || scale > 8) {
    throw new Error(`${label} must be greater than zero and at most 8`);
  }
  return scale;
}

export function parseModularCityAssetManifest(
  value: unknown,
): ModularCityAssetManifest {
  const manifest = requireRecord(value, "modular city manifest");
  if (manifest.schemaVersion !== 1) {
    throw new Error("modular city manifest schemaVersion must be 1");
  }
  if (!Array.isArray(manifest.archetypes) || manifest.archetypes.length === 0) {
    throw new Error("modular city manifest needs archetypes");
  }
  if (!Array.isArray(manifest.placements) || manifest.placements.length === 0) {
    throw new Error("modular city manifest needs placements");
  }
  const archetypeIds = new Set<string>();
  const archetypes = manifest.archetypes.map((raw, index) => {
    const entry = requireRecord(raw, `archetypes[${index}]`);
    const id = requireString(entry.id, `archetypes[${index}].id`);
    if (archetypeIds.has(id)) {
      throw new Error(`duplicate modular archetype ${id}`);
    }
    archetypeIds.add(id);
    return {
      id,
      assetUrl: requireString(entry.assetUrl, `archetypes[${index}].assetUrl`),
      lod1Url: requireString(entry.lod1Url, `archetypes[${index}].lod1Url`),
    };
  });
  const placementIds = new Set<string>();
  const placements = manifest.placements.map((raw, index) => {
    const entry = requireRecord(raw, `placements[${index}]`);
    const id = requireString(entry.id, `placements[${index}].id`);
    const archetypeId = requireString(
      entry.archetypeId,
      `placements[${index}].archetypeId`,
    );
    if (placementIds.has(id)) {
      throw new Error(`duplicate modular placement ${id}`);
    }
    if (!archetypeIds.has(archetypeId)) {
      throw new Error(`${id} uses unknown archetype ${archetypeId}`);
    }
    placementIds.add(id);
    const position = requireRecord(entry.position, `placements[${index}].position`);
    const scale = entry.scale === undefined
      ? null
      : requireRecord(entry.scale, `placements[${index}].scale`);
    return {
      id,
      label: requireString(entry.label, `placements[${index}].label`),
      archetypeId,
      category: requireString(entry.category, `placements[${index}].category`),
      tileId: requireString(entry.tileId, `placements[${index}].tileId`),
      position: {
        x: requireNumber(position.x, `placements[${index}].position.x`),
        y: requireNumber(position.y, `placements[${index}].position.y`),
        z: requireNumber(position.z, `placements[${index}].position.z`),
      },
      rotationYRadians: requireNumber(
        entry.rotationYRadians,
        `placements[${index}].rotationYRadians`,
      ),
      scale: scale === null
        ? { x: 1, y: 1, z: 1 }
        : {
            x: requireScaleNumber(scale.x, `placements[${index}].scale.x`),
            y: requireScaleNumber(scale.y, `placements[${index}].scale.y`),
            z: requireScaleNumber(scale.z, `placements[${index}].scale.z`),
          },
    };
  });
  return {
    schemaVersion: 1,
    sourceManifest: requireString(manifest.sourceManifest, "sourceManifest"),
    archetypes,
    placements,
  };
}

function templatesForScene(scene: Object3D): readonly TemplateMesh[] {
  scene.updateMatrixWorld(true);
  const templates: TemplateMesh[] = [];
  scene.traverse((child) => {
    if (!(child instanceof Mesh)) {
      return;
    }
    const index = child.geometry.getIndex();
    templates.push({
      geometry: child.geometry,
      material: child.material,
      matrix: child.matrixWorld.clone(),
      castShadow: child.userData.castShadow !== false,
      triangleCount: index === null
        ? child.geometry.getAttribute("position").count / 3
        : index.count / 3,
    });
  });
  return templates;
}

function bucketKey(placement: ModularCityAssetPlacement): string {
  return `${Math.floor(placement.position.x / VILNIUS_MODULAR_CITY_BUCKET_SIZE_M)}:${Math.floor(placement.position.z / VILNIUS_MODULAR_CITY_BUCKET_SIZE_M)}`;
}

export function squaredDistanceToModularCityBucket(
  focus: Readonly<{ x: number; z: number }>,
  center: Readonly<{ x: number; z: number }>,
): number {
  const halfSize = VILNIUS_MODULAR_CITY_BUCKET_SIZE_M * 0.5;
  const deltaX = Math.max(0, Math.abs(center.x - focus.x) - halfSize);
  const deltaZ = Math.max(0, Math.abs(center.z - focus.z) - halfSize);
  return deltaX * deltaX + deltaZ * deltaZ;
}

function placementsByBucket(
  placements: readonly ModularCityAssetPlacement[],
): ReadonlyMap<string, readonly ModularCityAssetPlacement[]> {
  const result = new Map<string, ModularCityAssetPlacement[]>();
  for (const placement of placements) {
    const key = bucketKey(placement);
    const bucket = result.get(key) ?? [];
    bucket.push(placement);
    result.set(key, bucket);
  }
  return result;
}

function createInstancedLevel(
  archetypeId: string,
  level: "lod0" | "lod1",
  capacity: number,
  templates: readonly TemplateMesh[],
): InstancedLevel {
  const result = new Group();
  result.name = `${archetypeId}:${level}`;
  const meshes: InstancedMesh[] = [];
  for (const [templateIndex, template] of templates.entries()) {
    const material = Array.isArray(template.material)
      ? [...template.material]
      : template.material;
    const mesh = new InstancedMesh(
      template.geometry,
      material as Material | Material[],
      capacity,
    );
    mesh.name = `${archetypeId}:${level}:mesh-${templateIndex}`;
    mesh.castShadow = level === "lod0" && template.castShadow;
    mesh.receiveShadow = true;
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    mesh.count = 0;
    mesh.visible = false;
    result.add(mesh);
    meshes.push(mesh);
  }
  return { object: result, meshes, templates };
}

function updateInstancedLevel(
  level: InstancedLevel,
  placements: readonly ModularCityAssetPlacement[],
  placementMatrices: ReadonlyMap<string, Matrix4>,
): void {
  const instanceMatrix = new Matrix4();
  for (const [templateIndex, mesh] of level.meshes.entries()) {
    const template = level.templates[templateIndex];
    if (template === undefined) continue;
    for (const [index, placement] of placements.entries()) {
      const placementMatrix = placementMatrices.get(placement.id);
      if (placementMatrix === undefined) continue;
      instanceMatrix.multiplyMatrices(placementMatrix, template.matrix);
      mesh.setMatrixAt(index, instanceMatrix);
    }
    mesh.count = placements.length;
    mesh.visible = placements.length > 0;
    if (mesh.visible) {
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingBox();
      mesh.computeBoundingSphere();
    }
  }
  level.object.visible = placements.length > 0;
}

async function loadScene(loader: GLTFLoader, url: string): Promise<Object3D> {
  try {
    return (await loader.loadAsync(url)).scene;
  } catch {
    return (await loader.loadAsync(url)).scene;
  }
}

export async function loadModularCityAssets(
  manifestUrl: string,
  fetcher: typeof fetch = fetch,
  options: Readonly<ModularCityAssetLoadOptions> = {},
): Promise<ModularCityAssetCollection> {
  const response = await fetcher(manifestUrl);
  if (!response.ok) {
    throw new Error(`Could not load modular city manifest (${response.status})`);
  }
  const manifest = parseModularCityAssetManifest(await response.json());
  const object = new Group();
  object.name = "vilnius-modular-city-assets";
  const loader = new GLTFLoader();
  const runtimes = new Map<string, ArchetypeRuntime>();
  const materials = new Set<Material>();
  const geometries = new Set<BufferGeometry>();
  const textures = new Set<Texture>();
  const materialBaseColors = new Map<MeshStandardMaterial, number>();
  const placementMatrices = new Map<string, Matrix4>();
  let activeSeason: EnvironmentSeason = "summer";
  let meshCount = 0;
  let templateTriangleCount = 0;
  let instancedTriangleCount = 0;
  let fullDetailArchetypeCount = 0;
  let visibleBucketCount = 0;
  let fullDetailBucketCount = 0;
  let disposed = false;
  let deferredStreamingEnabled = false;
  const pendingFullLoads = new Set<ArchetypeRuntime>();
  let fullLoadQueue: Promise<void> | null = null;
  let lastVisibilityFocus: Readonly<{ x: number; z: number }> | null = null;
  let lastVisibleDistanceMeters = Number.NaN;

  const placementPosition = new Vector3();
  const placementQuaternion = new Quaternion();
  const placementScale = new Vector3();
  for (const placement of manifest.placements) {
    placementPosition.set(
      placement.position.x,
      placement.position.y,
      placement.position.z,
    );
    placementQuaternion.setFromAxisAngle(
      WORLD_UP,
      placement.rotationYRadians,
    );
    placementScale.set(placement.scale.x, placement.scale.y, placement.scale.z);
    placementMatrices.set(
      placement.id,
      new Matrix4().compose(
        placementPosition,
        placementQuaternion,
        placementScale,
      ),
    );
  }

  const placementsByArchetype = new Map<string, ModularCityAssetPlacement[]>();
  for (const placement of manifest.placements) {
    const entries = placementsByArchetype.get(placement.archetypeId) ?? [];
    entries.push(placement);
    placementsByArchetype.set(placement.archetypeId, entries);
  }

  function registerTemplates(templates: readonly TemplateMesh[]): void {
    for (const template of templates) {
      geometries.add(template.geometry);
      const meshMaterials = Array.isArray(template.material)
        ? template.material
        : [template.material];
      for (const item of meshMaterials) {
        materials.add(item);
        for (const value of Object.values(item)) {
          if (value instanceof Texture) textures.add(value);
        }
        if (item instanceof MeshStandardMaterial) {
          const baseColor = item.color.getHex();
          materialBaseColors.set(item, baseColor);
          const treatment = authoredMaterialSeasonTreatment(item.name, activeSeason);
          item.color.setHex(baseColor).lerp(
            new Color(treatment.tint),
            treatment.strength,
          );
        }
      }
    }
  }

  async function loadSimplified(archetype: ModularCityAssetArchetype): Promise<void> {
    const placements = placementsByArchetype.get(archetype.id) ?? [];
    const scene = await loadScene(loader, archetype.lod1Url);
    if (disposed) return;
    const templates = templatesForScene(scene);
    registerTemplates(templates);
    const simplified = createInstancedLevel(
      archetype.id,
      "lod1",
      placements.length,
      templates,
    );
    object.add(simplified.object);
    const buckets: AssetBucket[] = [];
    for (const [key, bucketPlacements] of placementsByBucket(placements)) {
      const [bucketX, bucketZ] = key.split(":").map(Number);
      if (bucketX === undefined || bucketZ === undefined) {
        throw new Error(`Invalid modular city bucket key ${key}`);
      }
      buckets.push({
        key,
        center: {
          x: (bucketX + 0.5) * VILNIUS_MODULAR_CITY_BUCKET_SIZE_M,
          z: (bucketZ + 0.5) * VILNIUS_MODULAR_CITY_BUCKET_SIZE_M,
        },
        placements: bucketPlacements,
      });
    }
    meshCount += templates.length;
    templateTriangleCount += templates.reduce(
      (sum, template) => sum + template.triangleCount,
      0,
    );
    runtimes.set(archetype.id, {
      archetype,
      placements,
      buckets,
      simplified,
      full: null,
      fullTemplates: null,
      fullLoad: null,
      fullLoaded: false,
    });
  }

  for (
    let start = 0;
    start < manifest.archetypes.length;
    start += VILNIUS_MODULAR_CITY_ASSET_LOAD_CONCURRENCY
  ) {
    await Promise.all(
      manifest.archetypes.slice(
        start,
        start + VILNIUS_MODULAR_CITY_ASSET_LOAD_CONCURRENCY,
      ).map(loadSimplified),
    );
  }

  const categoryCounts = Object.freeze(Object.fromEntries(
    [...new Set(manifest.placements.map((placement) => placement.category))]
      .sort()
      .map((category) => [
        category,
        manifest.placements.filter((placement) => placement.category === category).length,
      ]),
  ));
  let deferredLoad: Promise<void> | null = null;

  async function loadFullArchetypeTemplates(
    runtime: ArchetypeRuntime,
  ): Promise<void> {
    if (runtime.fullLoaded) return;
    runtime.fullLoad ??= (async () => {
      const scene = await loadScene(loader, runtime.archetype.assetUrl);
      if (disposed) {
        scene.clear();
        return;
      }
      const templates = templatesForScene(scene);
      registerTemplates(templates);
      templateTriangleCount += templates.reduce(
        (sum, template) => sum + template.triangleCount,
        0,
      );
      runtime.fullTemplates = templates;
      runtime.full = createInstancedLevel(
        runtime.archetype.id,
        "lod0",
        runtime.placements.length,
        templates,
      );
      const warmupPlacement = runtime.placements[0];
      if (
        warmupPlacement !== undefined &&
        options.prepareObjectForDisplay !== undefined
      ) {
        updateInstancedLevel(
          runtime.full,
          [warmupPlacement],
          placementMatrices,
        );
        try {
          await options.prepareObjectForDisplay(runtime.full.object);
        } catch (error) {
          console.warn(
            `Could not prepare modular city asset ${runtime.archetype.id} before display.`,
            error,
          );
        }
        updateInstancedLevel(runtime.full, [], placementMatrices);
      }
      if (disposed) {
        return;
      }
      object.add(runtime.full.object);
      meshCount += templates.length;
      runtime.fullLoaded = true;
      fullDetailArchetypeCount += 1;
      lastVisibilityFocus = null;
    })();
    await runtime.fullLoad;
  }

  function queueFullLoad(runtime: ArchetypeRuntime): void {
    if (
      !deferredStreamingEnabled ||
      runtime.fullLoaded ||
      pendingFullLoads.has(runtime)
    ) {
      return;
    }
    pendingFullLoads.add(runtime);
    fullLoadQueue ??= (async () => {
      while (!disposed && pendingFullLoads.size > 0) {
        const next = pendingFullLoads.values().next().value as
          | ArchetypeRuntime
          | undefined;
        if (next === undefined) break;
        pendingFullLoads.delete(next);
        try {
          const loadFull = async (): Promise<void> => {
            if (disposed) return;
            await loadFullArchetypeTemplates(next);
          };
          await (options.scheduleDeferredTask?.(loadFull) ?? loadFull());
        } catch (error) {
          console.warn(
            `Deferred modular city asset ${next.archetype.id} failed to load.`,
            error,
          );
        }
        await new Promise<void>((resolve) => setTimeout(resolve, 20));
      }
      fullLoadQueue = null;
    })();
  }

  const diagnostics: ModularCityAssetDiagnostics = {
    archetypeCount: manifest.archetypes.length,
    placementCount: manifest.placements.length,
    get bucketCount(): number {
      return [...runtimes.values()].reduce(
        (sum, runtime) => sum + runtime.buckets.length,
        0,
      );
    },
    get meshCount(): number {
      return meshCount;
    },
    get templateTriangleCount(): number {
      return templateTriangleCount;
    },
    get instancedTriangleCount(): number {
      return instancedTriangleCount;
    },
    get fullDetailArchetypeCount(): number {
      return fullDetailArchetypeCount;
    },
    get visibleBucketCount(): number {
      return visibleBucketCount;
    },
    get fullDetailBucketCount(): number {
      return fullDetailBucketCount;
    },
    categoryCounts,
  };

  return {
    object,
    diagnostics,
    setSeason(season): void {
      activeSeason = season;
      for (const [item, baseColor] of materialBaseColors) {
        const treatment = authoredMaterialSeasonTreatment(item.name, season);
        item.color.setHex(baseColor).lerp(
          new Color(treatment.tint),
          treatment.strength,
        );
      }
    },
    update(
      focus,
      visibleDistanceMeters = VILNIUS_MODULAR_CITY_MAXIMUM_DISTANCE_M,
    ): void {
      const boundedVisibleDistanceMeters = Math.min(
        VILNIUS_MODULAR_CITY_MAXIMUM_DISTANCE_M,
        Math.max(
          0,
          visibleDistanceMeters + VILNIUS_MODULAR_CITY_VISIBILITY_MARGIN_M,
        ),
      );
      const refreshDistanceSquared =
        VILNIUS_MODULAR_CITY_VISIBILITY_REFRESH_DISTANCE_M ** 2;
      if (
        lastVisibilityFocus !== null &&
        (focus.x - lastVisibilityFocus.x) ** 2 +
          (focus.z - lastVisibilityFocus.z) ** 2 < refreshDistanceSquared &&
        Math.abs(
          boundedVisibleDistanceMeters - lastVisibleDistanceMeters,
        ) < 1
      ) {
        return;
      }
      lastVisibilityFocus = { x: focus.x, z: focus.z };
      lastVisibleDistanceMeters = boundedVisibleDistanceMeters;
      const fullDistanceSquared =
        VILNIUS_MODULAR_CITY_FULL_DETAIL_DISTANCE_M ** 2;
      const maximumDistanceSquared = boundedVisibleDistanceMeters ** 2;
      let nextVisibleBucketCount = 0;
      let nextFullDetailBucketCount = 0;
      let nextInstancedTriangleCount = 0;
      for (const runtime of runtimes.values()) {
        const simplifiedPlacements: ModularCityAssetPlacement[] = [];
        const fullPlacements: ModularCityAssetPlacement[] = [];
        const visibleBucketKeys = new Set<string>();
        const fullDetailBucketKeys = new Set<string>();
        let needsFullDetail = false;
        for (const placement of runtime.placements) {
          const deltaX = placement.position.x - focus.x;
          const deltaZ = placement.position.z - focus.z;
          const distanceSquared = deltaX * deltaX + deltaZ * deltaZ;
          if (distanceSquared > maximumDistanceSquared) continue;
          const placementBucketKey = bucketKey(placement);
          visibleBucketKeys.add(placementBucketKey);
          if (distanceSquared <= fullDistanceSquared) {
            needsFullDetail = true;
          }
          if (
            runtime.full !== null &&
            distanceSquared <= fullDistanceSquared
          ) {
            fullPlacements.push(placement);
            fullDetailBucketKeys.add(placementBucketKey);
          } else {
            simplifiedPlacements.push(placement);
          }
        }
        if (needsFullDetail && runtime.fullTemplates === null) {
          queueFullLoad(runtime);
        }
        nextVisibleBucketCount += visibleBucketKeys.size;
        nextFullDetailBucketCount += fullDetailBucketKeys.size;
        updateInstancedLevel(
          runtime.simplified,
          simplifiedPlacements,
          placementMatrices,
        );
        nextInstancedTriangleCount += runtime.simplified.templates.reduce(
          (sum, template) =>
            sum + template.triangleCount * simplifiedPlacements.length,
          0,
        );
        if (runtime.full !== null) {
          updateInstancedLevel(runtime.full, fullPlacements, placementMatrices);
          nextInstancedTriangleCount += runtime.full.templates.reduce(
            (sum, template) =>
              sum + template.triangleCount * fullPlacements.length,
            0,
          );
        }
      }
      visibleBucketCount = nextVisibleBucketCount;
      fullDetailBucketCount = nextFullDetailBucketCount;
      instancedTriangleCount = nextInstancedTriangleCount;
    },
    startDeferredStreaming(): void {
      deferredStreamingEnabled = true;
      lastVisibilityFocus = null;
    },
    async loadDeferred(): Promise<void> {
      deferredLoad ??= (async () => {
        for (const archetype of manifest.archetypes) {
          if (disposed) return;
          const runtime = runtimes.get(archetype.id);
          if (runtime !== undefined && !runtime.fullLoaded) {
            await loadFullArchetypeTemplates(runtime);
            await new Promise<void>((resolve) => setTimeout(resolve, 20));
          }
        }
      })();
      await deferredLoad;
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      object.removeFromParent();
      object.clear();
      for (const texture of textures) texture.dispose();
      for (const item of materials) item.dispose();
      for (const geometry of geometries) geometry.dispose();
      textures.clear();
      materials.clear();
      geometries.clear();
      materialBaseColors.clear();
      placementMatrices.clear();
      pendingFullLoads.clear();
      runtimes.clear();
    },
  };
}
